import { useEffect, useRef, useState } from 'react'
import { Button, Input, Modal, Progress, Select, Space, Tag, Typography, message } from 'antd'
import { useTranslation } from 'react-i18next'
import {
  CheckCircleFilled,
  CloseCircleFilled,
  InboxOutlined,
  LoadingOutlined,
  ReloadOutlined,
} from '@ant-design/icons'
import {
  completeUpload,
  initiateUpload,
  putFileToPresignedUrl,
  retryUploadPresign,
  uploadFileDirect,
  withTransientRetry,
  type UploadConfig,
  type UploadPlacementOpts,
} from '../api/client'

type FileStatus = 'queued' | 'uploading' | 'finalizing' | 'done' | 'failed'

interface UploadItem {
  key: string
  file: File
  status: FileStatus
  progress: number
  error?: string
  uploadId?: string
  /** Team mode: placement frozen when the file was added, so a whole batch
   * (and any retry) reuses the same target collection + visibility. */
  placement?: Pick<UploadPlacementOpts, 'collection_id' | 'visibility'>
}

/**
 * Team-mode upload placement (VistoeraTASK-00508). When provided the modal
 * shows 保存到 [资料库] / 可见范围 [团队共享|仅自己] and sends the choice with
 * every upload; the SERVER validates it (the collection must belong to this
 * Workspace). Omit entirely for personal Workspaces: the modal is then
 * identical to before.
 */
export interface UploadModalPlacement {
  collections: Array<{ id: string; name: string }>
  /** System 未整理图片 id (the default, i.e. "no explicit collection"). */
  unsortedId: string | null
  /** Preselected collection id (e.g. the collection currently entered). */
  defaultCollectionId: string | null
  canCreateCollection: boolean
  onCreateCollection: (name: string) => Promise<{ id: string; name: string }>
}

let keySeq = 0
const nextKey = () => `u${Date.now()}-${keySeq++}`

/**
 * Browser Direct Upload UI (Phase 1A spec §13/§19/§20/§21) — reuses the Home
 * page's existing modal-based import pattern rather than a whole new upload
 * page. Multi-file, concurrency-limited (per `config.max_concurrency`),
 * per-file progress from the real PUT-to-R2 progress event, and a per-file
 * "重试" that only re-requests a fresh presigned URL and re-PUTs the *same*
 * file — never a full-page refresh (spec §21).
 *
 * File bytes only ever travel Browser -> R2 (via `putFileToPresignedUrl`);
 * this backend only ever sees `initiate`/`retry`/`complete` calls (spec §14).
 *
 * The upload queue is driven entirely by passing item objects/keys through
 * closures (`queueRef` / `runUpload(item)`), deliberately never re-reading
 * "the current item for this key" back out of React state mid-flight — that
 * would race against the async setState/re-render cycle while several
 * uploads run concurrently.
 */
export default function UploadModal({
  open,
  config,
  onClose,
  onAnyCompleted,
  autoProcess = true,
  assetSource = 'browser_upload',
  title,
  placement,
}: {
  open: boolean
  config: UploadConfig | null
  onClose: () => void
  /** Called once per file as soon as its AssetChain is created — Home.tsx
   * uses this to refresh the chain list without waiting for the modal to
   * close (spec §19: 上传完成 -> 等待处理 should show up immediately). */
  onAnyCompleted: () => void
  /** Image Asset Center: false = library-only (no pipeline). Workbench keeps true. */
  autoProcess?: boolean
  assetSource?: string
  title?: string
  placement?: UploadModalPlacement
}) {
  const { t } = useTranslation(['autoProcess', 'actions', 'imageAssets'])
  const [collectionId, setCollectionId] = useState<string>('')
  const [visibility, setVisibility] = useState<'workspace' | 'private'>('workspace')
  const [newCollectionName, setNewCollectionName] = useState('')
  const [creatingCollection, setCreatingCollection] = useState(false)
  const modalTitle = title ?? t('upload.defaultTitle')
  const [items, setItems] = useState<UploadItem[]>([])
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const activeCountRef = useRef(0)
  const queueRef = useRef<UploadItem[]>([])

  useEffect(() => {
    if (!open) {
      setItems([])
      queueRef.current = []
      activeCountRef.current = 0
    } else if (placement) {
      setCollectionId(placement.defaultCollectionId || placement.unsortedId || '')
      setVisibility('workspace')
      setNewCollectionName('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const maxConcurrency = config?.max_concurrency || 4
  const maxSizeBytes = (config?.max_size_mb || 200) * 1024 * 1024
  const allowedMime = new Set(config?.allowed_mime || ['image/jpeg', 'image/png', 'image/webp'])

  const patch = (key: string, updates: Partial<UploadItem>) =>
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...updates } : it)))

  const pump = () => {
    while (activeCountRef.current < maxConcurrency && queueRef.current.length > 0) {
      const item = queueRef.current.shift()!
      activeCountRef.current += 1
      runUpload(item).finally(() => {
        activeCountRef.current -= 1
        pump()
      })
    }
  }

  const baseUploadOpts = { auto_process: autoProcess, asset_source: assetSource }
  const optsFor = (item: UploadItem): UploadPlacementOpts => ({ ...baseUploadOpts, ...(item.placement || {}) })

  const runUpload = async (item: UploadItem) => {
    const uploadOpts = optsFor(item)
    const key = item.key
    try {
      patch(key, { status: 'uploading', progress: 0 })
      if (config?.direct_to_r2) {
        const { data: initiated } = await withTransientRetry(() =>
          initiateUpload(item.file.name, item.file.type, item.file.size, uploadOpts),
        )
        patch(key, { uploadId: initiated.upload_id })
        await withTransientRetry(() =>
          putFileToPresignedUrl(initiated.upload_url, item.file, (pct) => patch(key, { progress: pct })),
        )
        patch(key, { status: 'finalizing', progress: 100 })
        await withTransientRetry(() => completeUpload(initiated.upload_id))
      } else {
        // R2 bucket CORS isn't configured for this origin — proxy the
        // bytes through this backend instead (see `UploadConfig.direct_to_r2`).
        // Retried on a transient backend/edge 502 (observed live during a
        // concurrent multi-file upload burst — see `withTransientRetry`),
        // never on a real rejection (bad mime/oversize/etc, always a 4xx).
        await withTransientRetry(() =>
          uploadFileDirect(item.file, (pct) => patch(key, { progress: pct }), uploadOpts),
        )
        patch(key, { status: 'finalizing', progress: 100 })
      }
      patch(key, { status: 'done' })
      onAnyCompleted()
    } catch (e) {
      patch(key, { status: 'failed', error: (e as Error).message || t('upload.uploadFailed') })
    }
  }

  const addFiles = (files: FileList | File[]) => {
    const accepted: UploadItem[] = []
    for (const file of Array.from(files)) {
      const mime = (file.type || '').toLowerCase()
      if (allowedMime.size && !allowedMime.has(mime)) {
        message.error(t('upload.unsupportedType', { filename: file.name, types: [...allowedMime].join(' / ') }))
        continue
      }
      if (file.size > maxSizeBytes) {
        message.error(t('upload.oversize', { filename: file.name, maxMb: config?.max_size_mb ?? 200 }))
        continue
      }
      accepted.push({
        key: nextKey(),
        file,
        status: 'queued',
        progress: 0,
        placement: placement
          ? {
              // unsorted == no explicit collection (server default)
              collection_id: collectionId && collectionId !== placement.unsortedId ? collectionId : null,
              visibility,
            }
          : undefined,
      })
    }
    if (!accepted.length) return
    setItems((prev) => [...prev, ...accepted])
    queueRef.current.push(...accepted)
    pump()
  }

  const retryOne = async (item: UploadItem) => {
    activeCountRef.current += 1
    try {
      patch(item.key, { status: 'uploading', progress: 0, error: undefined })
      if (config?.direct_to_r2) {
        const { data: fresh } = item.uploadId
          ? await withTransientRetry(() => retryUploadPresign(item.uploadId!))
          : await withTransientRetry(() =>
              initiateUpload(item.file.name, item.file.type, item.file.size, optsFor(item)),
            )
        const uploadId = item.uploadId || (fresh as { upload_id: string }).upload_id
        patch(item.key, { uploadId })
        await withTransientRetry(() =>
          putFileToPresignedUrl(fresh.upload_url, item.file, (pct) => patch(item.key, { progress: pct })),
        )
        patch(item.key, { status: 'finalizing', progress: 100 })
        await withTransientRetry(() => completeUpload(uploadId))
      } else {
        await withTransientRetry(() =>
          uploadFileDirect(item.file, (pct) => patch(item.key, { progress: pct }), optsFor(item)),
        )
        patch(item.key, { status: 'finalizing', progress: 100 })
      }
      patch(item.key, { status: 'done' })
      onAnyCompleted()
    } catch (e) {
      patch(item.key, { status: 'failed', error: (e as Error).message || t('upload.retryFailed') })
    } finally {
      activeCountRef.current -= 1
      pump()
    }
  }

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files)
  }

  const doneCount = items.filter((it) => it.status === 'done').length
  const failedCount = items.filter((it) => it.status === 'failed').length
  const busy = items.some((it) => it.status === 'uploading' || it.status === 'finalizing')

  return (
    <Modal
      title={modalTitle}
      open={open}
      onCancel={onClose}
      footer={
        <Space style={{ width: '100%', justifyContent: 'space-between', display: 'flex' }}>
          <span style={{ color: 'var(--muted)', fontSize: 12 }}>
            {items.length > 0
              ? t('upload.footerSummary', { total: items.length, done: doneCount, failed: failedCount })
              : ''}
          </span>
          <Button type="primary" onClick={onClose}>
            {busy ? t('upload.footerBusy') : t('upload.footerDone')}
          </Button>
        </Space>
      }
      width={640}
      destroyOnClose
    >
      <Typography.Paragraph type="secondary">
        {t('upload.description', { maxConcurrency })}
      </Typography.Paragraph>

      {placement && (
        <div className="upload-placement" data-testid="upload-placement">
          <label className="upload-placement-field">
            <span>{t('imageAssets:team.upload.saveTo')}</span>
            <Select
              value={collectionId || undefined}
              onChange={(v) => setCollectionId(v)}
              disabled={busy}
              style={{ minWidth: 180 }}
              options={placement.collections.map((c) => ({ value: c.id, label: c.name }))}
              data-testid="upload-placement-collection"
              dropdownRender={(menu) => (
                <>
                  {menu}
                  {placement.canCreateCollection && (
                    <div className="upload-placement-create" onMouseDown={(e) => e.stopPropagation()}>
                      <Input
                        size="small"
                        maxLength={60}
                        value={newCollectionName}
                        placeholder={t('imageAssets:team.upload.newCollectionPlaceholder')}
                        onChange={(e) => setNewCollectionName(e.target.value)}
                      />
                      <Button
                        size="small"
                        type="link"
                        loading={creatingCollection}
                        disabled={!newCollectionName.trim()}
                        onClick={async () => {
                          setCreatingCollection(true)
                          try {
                            const created = await placement.onCreateCollection(newCollectionName.trim())
                            setCollectionId(created.id)
                            setNewCollectionName('')
                          } catch (e) {
                            message.error((e as Error).message)
                          } finally {
                            setCreatingCollection(false)
                          }
                        }}
                      >
                        {t('imageAssets:team.upload.newCollectionOk')}
                      </Button>
                    </div>
                  )}
                </>
              )}
            />
          </label>
          <label className="upload-placement-field">
            <span>{t('imageAssets:team.upload.visibility')}</span>
            <Select
              value={visibility}
              onChange={(v) => setVisibility(v)}
              disabled={busy}
              style={{ minWidth: 140 }}
              options={[
                { value: 'workspace', label: t('imageAssets:team.share.shared') },
                { value: 'private', label: t('imageAssets:team.share.private') },
              ]}
              data-testid="upload-placement-visibility"
            />
          </label>
          {visibility === 'private' && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('imageAssets:team.upload.visibilityHintPrivate')}
            </Typography.Text>
          )}
        </div>
      )}

      <div
        className={`folder-picker-zone${dragOver ? ' folder-picker-zone-dragover' : ''}`}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault()
          setDragOver(true)
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        role="button"
        tabIndex={0}
      >
        <Space direction="vertical" size={8}>
          <InboxOutlined style={{ fontSize: 28, color: 'var(--accent)' }} />
          <span>{t('upload.dropHint')}</span>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t('upload.formatHint', { maxMb: config?.max_size_mb ?? 200 })}
          </Typography.Text>
        </Space>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={[...allowedMime].join(',')}
        style={{ display: 'none' }}
        onChange={(e) => {
          if (e.target.files?.length) addFiles(e.target.files)
          e.target.value = ''
        }}
        data-testid="upload-file-input"
      />

      {items.length > 0 && (
        <div className="upload-item-list">
          {items.map((it) => (
            <div key={it.key} className="upload-item-row" data-testid="upload-item-row">
              <span className="upload-item-name" title={it.file.name}>
                {it.file.name}
              </span>
              <span className="upload-item-progress">
                {it.status === 'queued' && <Tag>{t('upload.queued')}</Tag>}
                {it.status === 'uploading' && <Progress percent={it.progress} size="small" status="active" />}
                {it.status === 'finalizing' && (
                  <Tag icon={<LoadingOutlined spin />} color="processing">
                    {t('upload.finalizing')}
                  </Tag>
                )}
                {it.status === 'done' && (
                  <Tag icon={<CheckCircleFilled />} color="success">
                    {t('upload.uploaded')}
                  </Tag>
                )}
                {it.status === 'failed' && (
                  <Space size={4}>
                    <Tag icon={<CloseCircleFilled />} color="error">
                      {it.error || t('upload.failed')}
                    </Tag>
                    <Button
                      size="small"
                      icon={<ReloadOutlined />}
                      onClick={() => retryOne(it)}
                      data-testid="upload-item-retry-btn"
                    >
                      {t('actions:retry')}
                    </Button>
                  </Space>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </Modal>
  )
}
